import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getAssetSettings, updateAssetSettings } from '../../../models/assetsModel';
import { useHasAnyPermission } from '../../../utils/permissions';

function AssetSettings() {
  const canManageAssets = useHasAnyPermission(['Manage Assets']);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAssetSettings();
      if (data) {
        // Combine the two arrays into notification objects
        const assetNotifications = data.asset_notifications || [];
        const assetNotificationsEmail = data.asset_notifications_email || [];
        
        // Create notification objects
        const notificationList = [];
        const maxLength = Math.max(assetNotifications.length, assetNotificationsEmail.length);
        
        for (let i = 0; i < maxLength; i++) {
          notificationList.push({
            id: Date.now() + i, // Unique ID for React keys
            days: assetNotifications[i] || '',
            email: assetNotificationsEmail[i] || ''
          });
        }
        
        // If no notifications exist, add one empty row
        if (notificationList.length === 0) {
          notificationList.push({
            id: Date.now(),
            days: '',
            email: ''
          });
        }
        
        setNotifications(notificationList);
      }
    } catch (error) {
      console.error('Error fetching asset settings:', error);
      toast.error('Failed to load asset settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    
    if (!canManageAssets) {
      toast.error('You do not have permission to manage asset settings');
      return;
    }

    // Validate that all notifications have both days and email
    const validNotifications = notifications.filter(n => 
      n.days && n.days.toString().trim() !== '' && 
      n.email && n.email.trim() !== ''
    );

    if (notifications.length > 0 && validNotifications.length === 0) {
      toast.error('Please provide both days and email for at least one notification, or remove all notifications');
      return;
    }

    // Validate days are numbers and within range
    for (const notification of validNotifications) {
      const days = parseInt(notification.days);
      if (isNaN(days) || days < 1 || days > 365) {
        toast.error('Days must be a number between 1 and 365');
        return;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const notification of validNotifications) {
      if (!emailRegex.test(notification.email.trim())) {
        toast.error(`Invalid email format: ${notification.email}`);
        return;
      }
    }

    try {
      setSaving(true);
      
      // Convert to the format expected by the API
      // Keep arrays aligned - same index corresponds to same notification rule
      const asset_notifications = validNotifications.map(n => parseInt(n.days));
      const asset_notifications_email = validNotifications.map(n => n.email.trim());
      
      const settingsData = {
        asset_notifications,
        asset_notifications_email
      };
      
      console.log('Saving settings data:', settingsData);
      await updateAssetSettings(settingsData);
      toast.success('Asset settings updated successfully!');
    } catch (error) {
      console.error('Error saving asset settings:', error);
      toast.error('Failed to save asset settings');
    } finally {
      setSaving(false);
    }
  };

  const addNotification = () => {
    setNotifications(prev => [...prev, {
      id: Date.now(),
      days: '',
      email: ''
    }]);
  };

  const removeNotification = (index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  };



  if (!canManageAssets) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Access Denied</div>
          <div className="text-gray-600">You do not have permission to manage asset settings.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Asset Settings</h1>
                <p className="text-xs text-gray-500 mb-1">Configure license expiry notifications</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {notifications.length} notification{notifications.length !== 1 ? 's' : ''} configured
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchSettings}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center justify-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                  style={{ 
                    backgroundColor: '#0087D4', 
                    boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                  }}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* License Expiry Notifications Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-medium text-gray-800">License Expiry Notifications</h2>
            <p className="text-sm text-gray-600 mt-1">Configure when to send notifications before license expiry</p>
          </div>
          
          <div className="p-6">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-2">No notifications configured</p>
                <p className="text-gray-500 mb-4">Start by adding your first notification rule</p>
                <button
                  onClick={addNotification}
                  className="inline-flex items-center justify-center px-4 py-2 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                  style={{ 
                    backgroundColor: '#0087D4', 
                    boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add First Notification
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications.map((notification, index) => (
                  <div key={index} className="bg-white rounded-lg border-2 border-gray-200 p-6 shadow-sm">
                    <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                      <div className="w-full lg:w-48">
                        <label className="block text-sm font-semibold text-gray-800 mb-3">
                          Days before expiry
                        </label>
                        <input
                          type="number"
                          value={notification.days}
                          onChange={(e) => {
                            const newNotifications = [...notifications];
                            newNotifications[index].days = e.target.value;
                            setNotifications(newNotifications);
                          }}
                          className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                          placeholder="30"
                          min="1"
                          max="365"
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <label className="block text-sm font-semibold text-gray-800 mb-3">
                          Email recipient
                        </label>
                        <input
                          type="email"
                          value={notification.email}
                          onChange={(e) => {
                            const newNotifications = [...notifications];
                            newNotifications[index].email = e.target.value;
                            setNotifications(newNotifications);
                          }}
                          className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                          placeholder="admin@company.com"
                        />
                      </div>
                      
                      <div className="w-full lg:w-auto">
                        <button
                          onClick={() => removeNotification(index)}
                          disabled={notifications.length === 1}
                          className="w-full lg:w-auto inline-flex items-center justify-center px-4 py-2 border border-red-300 bg-white hover:bg-red-50 text-red-700 text-sm font-medium rounded-md shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex justify-center pt-4 border-t border-gray-200">
                  <button
                    onClick={addNotification}
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md shadow-sm transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Notification
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssetSettings;
